import React from "react";
import { Tag } from "@blueprintjs/core";
import { CSSProperties } from "react";
import { format_status_nivy } from "../../utils";
import moment from "moment";
import { StatusStore } from "../../stores/status/StatusStore";

const tagStyle: CSSProperties = {
  width: "70%",
  textAlign: "center"
};

/**
 * Retorna uma tag visual correspondente ao status informado.
 *
 * O texto e a classe CSS da tag são definidos conforme o status da tarefa e,
 * quando aplicável, pelo tipo da operação. Caso exista uma configuração
 * específica para o tipo informado, ela terá prioridade; caso contrário,
 * será utilizada a configuração padrão (`tipo == null`).
 *
 * Para tarefas de solicitação e download da SEFA com status de agendamento,
 * também é realizada a validação do prazo de 3 horas para definir se o
 * agendamento permanece válido ou se deve ser exibido como erro.
 *
 * @param status Status da tarefa.
 * @param tipo Tipo da operação utilizada para determinar o texto da tag.
 * @param row Dados da linha, utilizados para validações específicas de agendamento.
 * @param customStyles Estilos CSS adicionais aplicados à tag.
 * @returns Componente React `<Tag>` representando o status da tarefa.
 */
export function getTag(status: string | number, tipo: string, row?: any, customStyles?: CSSProperties) {
  let tagText = "";
  let className = "tagConfirmacao ";

  const tipo_ = (tipo || "").toLowerCase()

  const status_ = format_status_nivy(status);

  if (status_ == 11 && ["solicitacao_sefa", "download_sefa"].includes(tipo_)) {

    tagText = (moment((row.data_agendamento)).add(3, "hours").unix() < moment().unix())
      ? "ERRO AGENDAMENTO"
      : `AGENDAMENTO - ${moment(row.data_agendamento).add(3, "hours").format("DD/MM/YYYY")}`

    className += (moment((row.data_agendamento)).add(3, "hours").unix() < moment().unix())
      ? " tagNao"
      : " tagNovo"

    return (<Tag className={className} style={{ ...tagStyle, ...customStyles }}>{tagText}</Tag>);

  }

  const statusList = StatusStore.obs.status_all.filter(x => x.status == status_);

  const statusFilter = (statusList.find(x => x.tipo === tipo_)) ?? (statusList.find(x => x.tipo == null));

  if (!statusFilter) {

    tagText = "NÃO DISPONÍVEL"
    className += " tagNovo"

    return (<Tag className={className} style={{ ...tagStyle, ...customStyles }}>{tagText}</Tag>);

  }

  tagText = statusFilter.nome

  return (<Tag className={className} style={{ ...{ ...tagStyle, ...customStyles }, ...{ background: statusFilter.color, borderColor: statusFilter.color, color: "#fff" } }}>{tagText}</Tag>);

};

/**
 * Retorna a lista de opções de status para utilização em componentes de seleção.
 *
 * Para cada código de status é priorizada a configuração específica do tipo
 * informado. Caso não exista, é utilizada a configuração padrão
 * (`tipo == null`).
 *
 * A lista retornada é ordenada pelo código do status e inclui a opção
 * "TODOS" como primeiro item.
 *
 * @param tipo Tipo da operação utilizado para selecionar os rótulos específicos.
 * @returns Lista de opções no formato `{ value, label }`.
 */
export function getOptions(tipo?: string) {
  const statusList = StatusStore.obs.status_all;

  const statusIds = [...new Set(statusList.map(item => item.status))];

  const options = statusIds.map(status => {

    const statusFilter = statusList.find(item => item.status === status && item.tipo === tipo) ?? statusList.find(item => item.status === status && item.tipo == null);

    if (!statusFilter) {
      return null;
    }

    return {
      value: String(statusFilter.status),
      label: statusFilter.nome
    };

  })
    .filter((option): option is { value: string; label: string } => option !== null)
    .sort((a, b) => Number(a.value) - Number(b.value));

  return [{ value: "", label: "TODOS" }, ...options];

}

export const optionsTipo = () => {

  const listaUnica = [
    ...new Map(StatusStore.obs.status_all.map(item => [item.tipo, item])).values()
  ];

  return [
    { cod: "", descricao: "SELECIONE" },
    ...listaUnica.map(x => ({ cod: x.tipo, descricao: x.alias.toUpperCase() }))
  ];

}

export const options_java = [
  {cod: "cnds", descricao: "CNDS"},
  {cod: "fgts_digital", descricao: "FGTS DIGITAL"},
  {cod: "prefeitura", descricao: "NFS-E PREFEITURA"},
  {cod: "solicitacao_sefa", descricao: "SOLICITAÇÕES SEFA"},
  {cod: "download_sefa", descricao: "DOWNLOADS SEFA"},
  {cod: "dae_avulso", descricao: "DAE AVULSO"},
  {cod: "dei", descricao: "DEI"},
  {cod: "dec_sefa", descricao: "DEC SEFA"},
  {cod: "nfe_fazenda", descricao: "PORTAL NF-E"},
  {cod: "manifestacao_chaves", descricao: "MANIFESTAÇÃO CHAVES"},
  {cod: "parcelamentos", descricao: "PARCELAMENTOS"},
  {cod: "parcelamentos_sefa", descricao: "PARCELAMENTOS - SEFA"},
  {cod: "det", descricao: "DET"}
]
